import { Word } from '@/common/types/scopeAndSequence';
import { ControllerType } from '@/common/types/student';
import { getStudent } from '@/game/GameComponents/PhaserGame/GameInstance';
import { StudentGameInputHandler } from '@/game/GameComponents/PhaserGame/Input/StudentGameInputHandler';
import { waitUntil } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import DefinitionExampleView from '@/game/prefabs/GeneralUI/DefinitionExampleView';
import CardDropSite from '@/game/prefabs/StudentGame/CardDropSite';
import PlayerTypingInput from '@/game/prefabs/StudentGame/PlayerTypingInput';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import PlaceholderMinigameCard from '@/game/scenes/PlaceholderStudentGameCard';
import _ from 'lodash';

export default abstract class StudentGame extends Phaser.Scene {
  boardInteractive: boolean = false;
  inputHandler?: StudentGameInputHandler | undefined;
  hintTimerEvent?: Phaser.Time.TimerEvent;

  getScene(): Phaser.Scene {
    return this;
  }

  getInput(separator = ''): string {
    const control = this.getControlScheme(getStudent().settings.controllerType);
    switch (control) {
      case ControllerType.KEYBOARD:
        return this.getTypedInput();
      case ControllerType.CLICK:
      case ControllerType.DRAG:
        return this.getBoardCards()
          .map((card) => card.cardText)
          .join(separator);
    }
  }

  getTypedInput(): string {
    return this.getPlayerTypingInputs()[0].getCurrentPlayerInput();
  }

  // Return the player typing input in subclass.
  getPlayerTypingInputs(): PlayerTypingInput[] {
    throw new Error('Method not implemented.');
  }

  // Return the definition example view in subclass.
  getDefinitionExampleView(): DefinitionExampleView {
    throw new Error('Method not implemented.');
  }

  // Return the board drop site in subclass.
  getBoardDropSite(): CardDropSite {
    throw new Error('Method not implemented.');
  }

  // Return the hand drop site in subclass.
  getHandDropSite(): CardDropSite {
    throw new Error('Method not implemented.');
  }

  getDropSiteFromZone(zone: Phaser.GameObjects.Zone) {
    return zone === this.getHandDropSite().zone
      ? this.getHandDropSite()
      : this.getBoardDropSite();
  }

  getDropSiteFromCard(card: StudentGameCard | PlaceholderMinigameCard) {
    const handDropSite = this.getHandDropSite();
    const boardDropSite = this.getBoardDropSite();
    switch (card.parentContainer) {
      case handDropSite.DropSiteCardContainer:
        return handDropSite;
      case boardDropSite.DropSiteCardContainer:
        return boardDropSite;
      default:
        return undefined;
    }
  }

  getDropSiteFromName(name: string) {
    return name === 'hand' ? this.getHandDropSite() : this.getBoardDropSite();
  }

  getShouldDisplayBoardSlots(): boolean {
    throw new Error('Method not implemented.');
  }

  getControlScheme(controllerType: ControllerType): ControllerType {
    return controllerType;
  }

  setSubmissionPossible(_canSubmit: boolean): void {
    throw new Error('Method not implemented.');
  }

  getCanSubmit(): boolean {
    return this.boardInteractive && this.getInput().length > 0;
  }

  checkAnswer(): void {
    throw new Error('Method not implemented.');
  }

  clearAnticipation(exceptCard?: StudentGameCard): void {
    this.getAllCards()
      .filter((card) => card !== exceptCard)
      .forEach((card) => {
        card.setAnticipation(false);
      });
  }

  updateCandidatePlacement(
    _dropSite: CardDropSite,
    _card: StudentGameCard,
    _targetIndex: number,
  ): void {
    throw new Error('Method not implemented.');
  }

  setAllDropSitesVisible(visible: boolean) {
    this.getBoardDropSite().zone?.setVisible(visible);
    this.getHandDropSite().zone?.setVisible(visible);
  }

  setBoardInteractive(_interactive: boolean): void {
    throw new Error('Method not implemented.');
  }

  setupBoardForControlScheme(scheme: ControllerType) {
    this.getBoardDropSite().setVisible(
      scheme === ControllerType.CLICK || scheme === ControllerType.DRAG,
    );
    this.getHandDropSite().setVisible(
      scheme === ControllerType.CLICK || scheme === ControllerType.DRAG,
    );
    this.getPlayerTypingInputs().forEach((input: PlayerTypingInput) =>
      input.setVisible(scheme === ControllerType.KEYBOARD),
    );
  }

  clearCards() {
    this.getHandDropSite()?.layoutGroup?.reset();
    this.getBoardDropSite()?.layoutGroup?.reset();
  }

  async clearTypedInput() {
    await this.getPlayerTypingInputs().forEach((input: PlayerTypingInput) =>
      input.clearInput(),
    );
  }

  getHandCards(): StudentGameCard[] {
    return this.getHandDropSite()?.layoutGroup?.getMinigameCards() ?? [];
  }

  getBoardCards(): StudentGameCard[] {
    return this.getBoardDropSite()?.layoutGroup?.getMinigameCards() ?? [];
  }

  getAllCards() {
    return [
      ...(this.getHandDropSite()?.layoutGroup?.getMinigameCards() ?? []),
      ...(this.getBoardDropSite()?.layoutGroup?.getMinigameCards() ?? []),
    ];
  }

  manuallySelectCard(card: StudentGameCard) {
    this.inputHandler?.clickToPlaceInputSystem?.handleCardClicked(card, true);
  }

  showDefinitionForWord(word: Word) {
    this.getDefinitionExampleView().showHelperVisual(
      _.kebabCase(word.spelling),
    );
  }

  showExampleForWord(word: Word) {
    this.getDefinitionExampleView().showHelperVisual(_.kebabCase(word.example));
  }

  async preloadHelperIcons() {
    const definitionIcons = this.getDefinitionIconsToLoad();
    const exampleIcons = this.getExampleIconsToLoad();

    let isLoading = false;
    definitionIcons.forEach((iconKey) => {
      if (!this.getScene().cache.json.exists(iconKey)) {
        this.getScene().load.image(
          iconKey,
          `game_assets/definition_images/${iconKey}.png`,
        );
        isLoading = true;
      }
    });

    exampleIcons.forEach((iconKey) => {
      if (!this.getScene().cache.json.exists(iconKey)) {
        this.getScene().load.image(
          iconKey,
          `game_assets/example_images/${iconKey}.png`,
        );
        isLoading = true;
      }
    });

    if (getStudent().settings.articulatoryGesturesEnabled) {
      if (!this.getScene().cache.json.exists('articulatory-gestures')) {
        this.getScene().load.pack(
          'articulatory-gestures',
          'game_assets/sprites/phaser_game/phaser_game_ui/articulatory-gestures/articulatory-gestures.json',
        );
        isLoading = true;
      }
    }

    this.getScene().load.once('complete', () => {
      isLoading = false;
    });
    this.getScene().load.start();

    await waitUntil(this.time, () => isLoading === false);
  }

  getDefinitionIconsToLoad(): string[] {
    return [];
  }

  getExampleIconsToLoad(): string[] {
    return [];
  }

  shouldShowCardGuidance(): boolean {
    return true;
  }
}
